import connectShopifyIcon from '@/assets/images/connectShopify.svg';
import { Button, Icon } from '@common/index';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import React, {
    forwardRef,
    useCallback, useEffect,
    useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import AccountList, { ShopifyStore } from './AccountList';
import styles from './styles.module.scss';

interface ConnectShopifyProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CONNECT_SHOPIFY_STORAGE_KEY = 'bottom-up-shopify-list';

const ConnectShopify: React.FC<ConnectShopifyProps> = ({ isOpen, onClose }) => {
    const [shopUrl, setShopUrl] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get('redirect');
    const [connectAnother, setConnectAnother] = useState(false);
    const [stores, setStores] = useLocalStorage<ShopifyStore[]>(CONNECT_SHOPIFY_STORAGE_KEY, [], {
        serializer: JSON.stringify,
        deserializer: JSON.parse,
    });
    const redirect_url = redirect ||  `${location.origin}/admin`;

    const registerShopify = useCallback(async (shop_domain: string, redirect_url: string, shopify_token?: string) => {
        const api = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/shopify/register`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Shopify-Token': shopify_token || ''},
            body: JSON.stringify({
                shop_domain,
                redirect_url,
            })
        })
        const res = await api.json();
        const authUrl = res.data?.authorizationUrl;
        if(authUrl) {
            window.location.href = authUrl;
        }else{
            throw new Error('Connect to Shopify failed. Please try again.');
        }
    }, []);

    const handleConnect = useCallback(async () => {
        setConnectAnother(true);
        // shopUrl 现在直接是店铺名，不需要再提取
        const domain = shopUrl.trim();
        if (!domain) {
            setError('Please enter a valid Shopify store name');
            return;
        }

        setIsConnecting(true);
        setError('');
        try {
            await registerShopify(`${domain}.myshopify.com`, redirect_url);
        } catch (err) {
            console.error('连接 Shopify 失败:', err);
            setIsConnecting(false);
            setError('Failed to connect to Shopify store. Please try again.');
        }
    }, [shopUrl, registerShopify, redirect_url]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && shopUrl.trim()) {
            handleConnect();
        }
    }, [handleConnect, shopUrl]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setShopUrl('');
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    if(!connectAnother && stores.length){
        return (
            <AccountList 
                stores={stores}
                onConnectAnother={() => {
                    setConnectAnother(true);
                }}
                onStoreSelect={(store: ShopifyStore) => {
                    registerShopify(store.storeUrl, redirect_url, store.storeToken);
                }}
            />
        );
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.container}>
                <div className={styles.content}>
                    <div className={styles.iconContainer}>
                        <img src={connectShopifyIcon} alt="Shopify Icon" />
                    </div>
                    <div className={styles.titleSection}>
                        <h2 className={styles.title}>Connect to Shopify</h2>
                        <p className={styles.subtitle}>Connect your store to personalize your Bustly experience</p>
                    </div>
                    <ShopifyUrlInput
                        value={shopUrl}
                        onChange={(value) => {
                            setShopUrl(value);
                            setError('');
                        }}
                        onKeyPress={handleKeyPress}
                        error={error}
                    />
                </div>
                <div className={styles.footer}>
                    {stores.length ? (
                        <Button 
                            onClick={() => {
                                setConnectAnother(false);
                            }}
                            color="gray"
                            style={{
                                width: 180,
                                height: 32,
                                alignSelf: 'center'
                            }}
                            variant="outline"
                        >
                            Back
                        </Button>
                    ) : null}
                    <Button 
                        onClick={handleConnect}
                        style={{
                            width: 180,
                            height: 32,
                            alignSelf: 'center'
                        }}
                        loading={isConnecting}
                    >
                        Connect
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ShopifyUrlInput Component
interface ShopifyUrlInputProps {
    value: string;
    onChange: (value: string) => void;
    onKeyPress?: (e: React.KeyboardEvent) => void;
    disabled?: boolean;
    error?: string;
    showIcon?: boolean;
    label?: string;
    placeholder?: string;
    showTooltip?: boolean;
    tooltipText?: string;
}

export const ShopifyUrlInput = forwardRef<HTMLInputElement, ShopifyUrlInputProps>((
    {
        value,
        onChange,
        onKeyPress,
        disabled = false,
        error,
        showIcon,
        label = 'Shopify Store URL',
        placeholder = 'Your store URL',
        showTooltip = true,
        tooltipText = 'Login to your Shopify and navigate down to Domains section.'
    },
    ref
) => {
    const [validationError, setValidationError] = useState('');

    const extractShopifyDomain = useCallback((url: string): string | null => {
        try {
            // Remove leading @ symbol if present
            const cleanUrl = url.startsWith('@') ? url.slice(1) : url;

            // Add https:// if no protocol
            const urlWithProtocol = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;

            // Parse URL
            const urlObj = new URL(urlWithProtocol);

            // Check if it's a myshopify.com domain
            if (!urlObj.hostname.endsWith('.myshopify.com')) {
                throw new Error('Invalid Shopify store URL');
            }

            // Extract shop name
            return urlObj.hostname.split('.')[0];
        } catch (err) {
            return null;
        }
    }, []);

    const validateShopUrl = useCallback((input: string): string | null => {
        if (!input.trim()) {
            return 'Please enter your Shopify store name';
        }

        // If input contains full URL, extract shop name for validation
        let shopName = input;
        if (input.includes('.myshopify.com')) {
            const domain = extractShopifyDomain(input);
            if (!domain) {
                return 'Please enter a valid Shopify store URL (e.g., yourstore.myshopify.com)';
            }
            shopName = domain;
        }

        // Validate shop name format
        if (shopName.length < 3) {
            return 'Store name must be at least 3 characters long';
        }

        // Validate shop name contains only letters, numbers, and hyphens
        if (!/^[a-zA-Z0-9-]+$/.test(shopName)) {
            return 'Store name can only contain letters, numbers, and hyphens';
        }

        // Validate doesn't start or end with hyphen
        if (shopName.startsWith('-') || shopName.endsWith('-')) {
            return 'Store name cannot start or end with a hyphen';
        }

        return null;
    }, [extractShopifyDomain]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        // Check if full Shopify URL was pasted, if so extract shop name
        let processedValue = inputValue;
        if (inputValue.includes('.myshopify.com')) {
            const extractedDomain = extractShopifyDomain(inputValue);
            if (extractedDomain) {
                processedValue = extractedDomain;
            }
        }
        
        onChange(processedValue);
        setValidationError('');
        
        // Real-time validation
        if (processedValue.trim()) {
            const validationErr = validateShopUrl(processedValue);
            if (validationErr) {
                setValidationError(validationErr);
            }
        }
    }, [onChange, validateShopUrl, extractShopifyDomain]);

    const displayError = error || validationError;

    return (
        <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
                {showIcon ? <Icon name="shopify" /> : null}
                {label}
                {showTooltip ? <Icon name="info" tips={tooltipText} /> : null}
            </label>
            <div
                className={`${styles.inputWrapper} ${
                    displayError ? styles.error 
                        : ''
                }`}
            >
                <div className={styles.urlPrefix}>https://</div>
                <input
                    ref={ref}
                    type="text"
                    className={styles.input}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={onKeyPress}
                    disabled={disabled}
                    autoFocus
                />
                <div className={styles.urlSuffix}>.myshopify.com</div>
            </div>
        
            {displayError ? (
                <div className={styles.validationError}>
                    <ExclamationTriangleIcon width={14} height={14} />
                    {displayError}
                </div>
            ) : null}
        </div>
    );
});

export { default as AccountList } from './AccountList';
export type { AccountListProps, ShopifyStore } from './AccountList';
export default ConnectShopify;
